import { useEffect, useRef, useState, type FormEvent } from 'react';
import { X } from 'lucide-react';
import type { Category } from '../../types';
import { createLocalId, normalizeCategoryName } from '../../types';

type Props = {
  categories: readonly Category[];
  category: Category | null;
  onClose: () => void;
  onSave: (category: Category) => Promise<void>;
  open: boolean;
};

export const CategoryDialog = ({ categories, category, onClose, onSave, open }: Props) => {
  const dialogRef = useRef<HTMLDialogElement>(null);
  const [name, setName] = useState('');
  const [color, setColor] = useState('#da8825');
  const [error, setError] = useState('');
  const [saving, setSaving] = useState(false);

  useEffect(() => { if (open) { setName(category?.name ?? ''); setColor(category?.color ?? '#da8825'); setError(''); } }, [category, open]);
  useEffect(() => { const dialog = dialogRef.current; if (!dialog) return; if (open && !dialog.open) dialog.showModal(); if (!open && dialog.open) dialog.close(); }, [open]);

  const submit = async (event: FormEvent) => {
    event.preventDefault();
    const cleanName = name.trim();
    const normalizedName = normalizeCategoryName(cleanName);
    if (!cleanName) return setError('Category name is required.');
    if (categories.some((item) => item.id !== category?.id && item.normalizedName === normalizedName)) return setError('A category with this name already exists.');
    setSaving(true);
    try {
      await onSave({ id: category?.id ?? createLocalId('category'), name: cleanName, normalizedName, color, icon: category?.icon ?? null, kind: category?.kind ?? 'custom' });
      onClose();
    } catch (saveError) {
      setError(saveError instanceof Error ? saveError.message : 'The category could not be saved.');
    } finally {
      setSaving(false);
    }
  };

  return <dialog className="category-dialog" ref={dialogRef} onCancel={onClose}><form className="entry-form" onSubmit={submit}><div className="dialog-heading"><div><p className="eyebrow">Category</p><h2>{category ? 'Edit category' : 'New category'}</h2></div><button type="button" className="icon-button" aria-label="Close category form" onClick={onClose}><X size={20} /></button></div><div className="form-grid"><label className="field field-wide"><span>Name *</span><input autoFocus required maxLength={80} value={name} onChange={(event) => setName(event.target.value)} /></label><label className="field"><span>Color</span><input type="color" value={color} onChange={(event) => setColor(event.target.value)} /></label></div>{error && <p className="form-error" role="alert">{error}</p>}<div className="dialog-actions"><button type="button" className="secondary-button" onClick={onClose}>Cancel</button><button type="submit" className="primary-button" disabled={saving}>{saving ? 'Saving...' : 'Save category'}</button></div></form></dialog>;
};
